import React from 'react';
import {
  AlertCircle,
  Calendar,
  FileText,
  MapPin,
  Truck,
} from 'lucide-react';
import { ExportMenu } from '../../ExportMenu';
import type {
  CarrierProfile as CarrierProfileType,
  Crash,
  Inspection,
  Vehicle,
  Violation,
} from '../../../types/api';
import type { BASICScore } from '../CarrierTabs.types';
import type {
  ViolationsBySeverity,
  ViolationsSummary,
} from './Violations';

export interface OverviewProps {
  profile: CarrierProfileType;
  violations: Violation[];
  inspections: Inspection[];
  crashes: Crash[];
  basics: BASICScore[];
  vehicles: Vehicle[];
  violationsSummary: ViolationsSummary | null;
  violationsBySeverity: ViolationsBySeverity;
  expandedViolationSeverity: string | null;
  setExpandedViolationSeverity: (severity: string | null) => void;
  setSelectedVin: (vin: string | null) => void;
  setSelectedInspection: (inspection: Inspection | null) => void;
}

type Tone = 'risk' | 'warn' | 'ok' | 'neutral';

const TONE_COLOR: Record<Tone, string> = {
  risk: 'var(--color-danger)',
  warn: 'var(--color-warning)',
  ok: 'var(--color-success)',
  neutral: 'var(--color-gray-400)'
};

interface StatCell {
  key: string;
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
  tone?: Tone;
  onClick?: () => void;
  isSelected?: boolean;
}

/** Washi stat row: one hairline-ruled strip instead of a box per number. */
const StatStrip: React.FC<{ cells: StatCell[] }> = ({ cells }) => (
  <div
    className="grid grid-cols-2 lg:grid-cols-[repeat(var(--cols),minmax(0,1fr))] border-y border-gray-200"
    style={{ '--cols': cells.length } as React.CSSProperties}
  >
    {cells.map((cell, i) => {
      const body = (
        <>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 tracking-[0.04em]">
            {cell.tone && cell.tone !== 'neutral' && (
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: TONE_COLOR[cell.tone] }} aria-hidden="true" />
            )}
            {cell.label}
          </div>
          <div className="mt-1 font-serif text-[28px] leading-tight text-gray-900 tabular-nums">{cell.value}</div>
          {cell.note && <div className="mt-0.5 text-xs text-gray-400">{cell.note}</div>}
        </>
      );
      // Divider before every cell except the first; on the 2-column mobile grid only before odd cells
      const divider = i === 0 ? '' : i % 2 === 1 ? 'pl-5 border-l' : 'lg:pl-5 lg:border-l';
      const cellClass = `text-left py-4 pr-4 ${divider} border-gray-200`;
      return cell.onClick ? (
        <button
          key={cell.key}
          type="button"
          onClick={cell.onClick}
          aria-pressed={cell.isSelected}
          className={`${cellClass} cursor-pointer transition-colors hover:bg-gray-100/60 ${cell.isSelected ? 'shadow-[inset_0_-2px_0_var(--color-gray-900)]' : ''}`}
        >
          {body}
        </button>
      ) : (
        <div key={cell.key} className={cellClass}>{body}</div>
      );
    })}
  </div>
);

const SectionHeading: React.FC<{ title: string; aside?: string }> = ({ title, aside }) => (
  <div className="flex items-baseline justify-between gap-4 mb-3">
    <h3 className="font-serif text-base text-gray-900">{title}</h3>
    {aside && <span className="text-xs text-gray-400">{aside}</span>}
  </div>
);

/** OOS rate vs national average; neutral when the average is unknown rather than comparing against 0. */
const oosCell = (key: string, label: string, rate: string | number | null | undefined, avg: string | null | undefined): StatCell => {
  const value = parseFloat(String(rate ?? '0'));
  const avgValue = avg !== null && avg !== undefined && avg !== '' ? parseFloat(avg) : NaN;
  const hasAvg = !Number.isNaN(avgValue);
  return {
    key,
    label,
    value: `${(Number.isNaN(value) ? 0 : value).toFixed(2)}%`,
    note: hasAvg ? `National avg ${avgValue.toFixed(1)}%` : 'National avg unavailable',
    tone: !hasAvg ? 'neutral' : value <= avgValue ? 'ok' : 'risk'
  };
};

const insuranceStatus = (requiredFlag: string | number | null | undefined, onFile: number, requiredAmount?: number): { text: string; tone: Tone } | null => {
  const required = requiredFlag === null || requiredFlag === undefined ? requiredFlag : String(requiredFlag);
  if (required === 'Y') {
    const sufficient = requiredAmount !== undefined ? onFile >= requiredAmount && onFile > 0 : onFile > 0;
    if (requiredAmount !== undefined) return sufficient ? { text: 'Compliant', tone: 'ok' } : { text: 'Insufficient', tone: 'risk' };
    return sufficient ? { text: 'On file', tone: 'ok' } : { text: 'Missing', tone: 'risk' };
  }
  if (required === 'N') return { text: 'Not required', tone: 'neutral' };
  return null;
};

const Overview: React.FC<OverviewProps> = ({
  profile,
  violations,
  inspections,
  crashes,
  basics,
  vehicles,
  violationsSummary,
  violationsBySeverity,
  expandedViolationSeverity,
  setExpandedViolationSeverity,
  setSelectedVin,
  setSelectedInspection,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Export Buttons - Comprehensive Report */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
        <ExportMenu
          dataType="all"
          data={null}
          carrierName={profile?.legalName}
          dotNumber={profile?.dotNumber}
          allData={{
            profile,
            violations,
            inspections,
            crashes,
            basics,
            vehicles,
          }}
          buttonText="Export All Data"
          size="sm"
        />
      </div>
    
      {/* Safety Performance Metrics */}
      <section className="card-modern">
        <SectionHeading title="Safety performance" aside="FMCSA · 24-month window" />
        <StatStrip
          cells={[
            oosCell('driver-oos', 'Driver OOS rate', profile.driverOosRate, profile.driverOosRateNationalAverage),
            oosCell('vehicle-oos', 'Vehicle OOS rate', profile.vehicleOosRate, profile.vehicleOosRateNationalAverage),
            {
              key: 'crashes',
              label: 'Crashes · 24 mo',
              value: profile.crashTotal ?? 0,
              note: `${profile.fatalCrash ?? 0} fatal · ${profile.injCrash ?? 0} injury`
            },
            {
              key: 'inspections',
              label: 'Driver inspections · 24 mo',
              value: profile.driverInsp ?? 0,
              note: `${profile.vehicleInsp ?? 0} vehicle inspections`
            }
          ]}
        />
        <p className="mt-3 text-xs text-gray-400">For complete historical data, see the Analytics tab.</p>
      </section>

      {/* Violation Summary */}
      {violations.length > 0 && (
        <section className="card-modern">
          <SectionHeading title="Violations by severity" aside="Select a severity to list its violations" />
          <StatStrip
            cells={([
              ['CRITICAL', 'Critical', 'risk', violationsSummary?.bySeverity.critical],
              ['HIGH', 'High', 'warn', violationsSummary?.bySeverity.high],
              ['MEDIUM', 'Medium', 'warn', violationsSummary?.bySeverity.medium],
              ['LOW', 'Low', 'neutral', violationsSummary?.bySeverity.low]
            ] as const).map(([severity, label, tone, summaryCount]) => {
              const listed = violationsBySeverity[severity].length;
              const isSelected = expandedViolationSeverity === severity;
              return {
                key: severity,
                label,
                tone,
                value: summaryCount ?? listed,
                note: listed > 0 ? (isSelected ? 'Hide list' : 'View violations') : '—',
                isSelected,
                onClick: listed > 0
                  ? () => setExpandedViolationSeverity(isSelected ? null : severity)
                  : undefined
              };
            })}
          />

          {/* Expanded Violation Details */}
          {expandedViolationSeverity && violationsBySeverity[expandedViolationSeverity as keyof typeof violationsBySeverity].length > 0 && (
            <div className="card-modern" style={{
              marginTop: 'var(--space-lg)',
              background: '#f5f2ea',
              borderLeft: `4px solid ${
                expandedViolationSeverity === 'CRITICAL' ? 'var(--color-danger)' :
                expandedViolationSeverity === 'HIGH' ? '#7d561c' :
                expandedViolationSeverity === 'MEDIUM' ? 'var(--color-warning)' :
                'var(--color-success)'
              }`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
                <h4 style={{ fontWeight: 600, color: 'var(--color-gray-900)' }}>
                  {expandedViolationSeverity} Violations ({violationsBySeverity[expandedViolationSeverity as keyof typeof violationsBySeverity].length})
                </h4>
                <button
                  onClick={() => setExpandedViolationSeverity(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-gray-500)'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-1">
                  {violationsBySeverity[expandedViolationSeverity as keyof typeof violationsBySeverity].map((violation, idx) => {
                    const isOOS = violation.outOfServiceIndicator === 'Y' || violation.outOfService === true;
                    const unitType = violation.inspViolUnit === 'D' ? 'Driver' :
                                     violation.unitType === '11' || violation.unitType === 'TRUCK' ? 'Truck' :
                                     violation.unitType === '9' || violation.unitType === 'TRAILER' ? 'Trailer' : 'Vehicle';
    
                    return (
                      <div key={idx} className="w-full max-w-[600px] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                        <div className={`border-l-[4px] ${isOOS ? 'border-red-500' : 'border-green-500'}`}>
                          <div className="p-[10px]">
                            {/* Header: Badges */}
                            <div className="flex flex-wrap items-center gap-1 mb-1.5">
                              <span className="px-1 py-[2px] bg-slate-100 text-slate-700 text-xs font-semibold rounded border border-slate-200 font-mono">
                                {violation.violationCode || violation.violation_code || (violation.partNo && violation.partNoSection ? `${violation.partNo}.${violation.partNoSection}` : 'N/A')}
                              </span>
    
                              {isOOS && (
                                <span className="px-1 py-[2px] bg-red-50 text-red-700 text-xs font-bold rounded border border-red-100 flex items-center gap-[2px]">
                                  <AlertCircle size={12} /> OOS
                                </span>
                              )}
    
                              <span className="px-1 py-[2px] bg-gray-50 text-gray-500 text-xs font-medium rounded border border-gray-100">
                                {violation.basicCategory || 'Unknown'}
                              </span>
    
                              <span className="px-1 py-[2px] bg-indigo-50 text-indigo-600 text-xs font-medium rounded border border-indigo-100">
                                {unitType}
                              </span>
                            </div>
    
                            {/* Main Description */}
                            <h3 className="text-gray-900 font-semibold text-lg mb-2 leading-tight">
                              {violation.description || violation.violation_description || 'Violation description not available'}
                            </h3>
    
                            {/* Vehicle Card - Compact Gray Box */}
                            <div className="bg-slate-50 rounded-md border border-slate-100 p-[6px] mb-2">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-1 mb-[2px]">
                                    <Truck size={14} className="text-slate-400" />
                                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicle Details</span>
                                  </div>
                                  {violation.vin && violation.vin !== 'N/A' ? (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedVin(violation.vin);
                                      }}
                                      className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                                    >
                                      {violation.vin}
                                    </button>
                                  ) : (
                                    <div className="font-mono text-sm font-medium text-slate-400">VIN not available</div>
                                  )}
                                  {violation.unitMake && (
                                    <div className="text-xs text-slate-500 mt-[2px]">
                                      Unit: {violation.inspViolUnit === 'D' ? 'Driver' : `Vehicle ${violation.inspViolUnit}`}
                                      {violation.unitMake && ` - ${violation.unitMake}`}
                                    </div>
                                  )}
                                </div>
                                {violation.allUnits && violation.allUnits.length > 1 && (
                                  <div className="text-xs font-medium bg-white text-slate-500 px-1 py-[2px] rounded shadow-sm border border-slate-200">
                                    {violation.allUnits.length} Units Insp.
                                  </div>
                                )}
                              </div>
                            </div>
    
                            {/* Footer: Inspection Meta Data */}
                            <div className="flex flex-wrap items-center justify-between pt-2 border-t border-gray-100 text-sm">
                              <div className="flex items-center gap-2">
                                {violation.inspectionId && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const inspection = inspections.find((i) => i.inspectionId === violation.inspectionId);
                                      if (inspection) {
                                        setSelectedInspection(inspection);
                                      }
                                    }}
                                    className="flex items-center gap-[3px] group cursor-pointer hover:opacity-80"
                                  >
                                    <FileText size={14} className="text-blue-500" />
                                    <span className="font-medium text-blue-600 group-hover:text-blue-700 underline decoration-blue-200 underline-offset-2">
                                      #{violation.inspectionId}
                                    </span>
                                  </button>
                                )}
                                <div className="flex items-center gap-[3px] text-gray-500">
                                  <Calendar size={14} />
                                  <span>{violation.inspectionDate ? new Date(violation.inspectionDate).toLocaleDateString() : 'N/A'}</span>
                                </div>
                              </div>
    
                              <div className="flex items-center gap-[3px] text-gray-500 mt-1 sm:mt-0">
                                <MapPin size={14} />
                                <span className="uppercase text-[11px] font-bold tracking-wide bg-gray-100 px-[3px] py-[1px] rounded text-gray-600">
                                  {violation.inspectionState || 'N/A'}
                                </span>
                                <span className="text-xs font-medium truncate max-w-[150px]" title={violation.inspectionLocation}>
                                  {violation.inspectionLocation || 'Location not available'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    
      {/* Insurance Information */}
      <section className="card-modern">
        <SectionHeading title="Insurance" aside="FMCSA filings · USD" />
        {(() => {
          const bipdOnFile = parseInt(String(profile.bipdInsuranceOnFile || '0')) * 1000;
          const bipdRequired = parseInt(String(profile.bipdRequiredAmount || '0')) * 1000;
          const cargoOnFile = parseInt(String(profile.cargoInsuranceOnFile || '0')) * 1000;
          const bondOnFile = parseInt(String(profile.bondInsuranceOnFile || '0'));
          const bipdStatus = insuranceStatus(profile.bipdInsuranceRequired, bipdOnFile, bipdRequired);
          const cargoStatus = insuranceStatus(profile.cargoInsuranceRequired, cargoOnFile);
          const bondStatus = insuranceStatus(profile.bondInsuranceRequired, bondOnFile);
          const requiredNote = (requiredFlag: string | number | null | undefined, amount?: number) =>
            String(requiredFlag) === 'Y' ? (amount ? `Required $${amount.toLocaleString()}` : 'Required') : String(requiredFlag) === 'N' ? 'Not required' : 'Requirement unknown';
          return (
            <StatStrip
              cells={[
                {
                  key: 'bipd',
                  label: bipdStatus ? `BIPD liability · ${bipdStatus.text}` : 'BIPD liability',
                  tone: bipdStatus?.tone,
                  value: `$${bipdOnFile.toLocaleString()}`,
                  note: requiredNote(profile.bipdInsuranceRequired, bipdRequired)
                },
                {
                  key: 'cargo',
                  label: cargoStatus ? `Cargo · ${cargoStatus.text}` : 'Cargo',
                  tone: cargoStatus?.tone,
                  value: `$${cargoOnFile.toLocaleString()}`,
                  note: requiredNote(profile.cargoInsuranceRequired)
                },
                {
                  key: 'bond',
                  label: bondStatus ? `Bond / surety · ${bondStatus.text}` : 'Bond / surety',
                  tone: bondStatus?.tone,
                  value: `$${bondOnFile.toLocaleString()}`,
                  note: requiredNote(profile.bondInsuranceRequired)
                }
              ]}
            />
          );
        })()}
        <p className="mt-3 text-xs text-gray-400">BIPD = bodily injury and property damage liability coverage.</p>
      </section>
    </div>
  );
};

export default Overview;
